"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCircleX, IconShieldExclamation } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import posthogClient from "posthog-js";
import { useState } from "react";
import { toast } from "sonner";
import { getScreenshotQueryKey, isAwaitingScheduledRetry, isTerminalState, MAX_CONSECUTIVE_FAILURES, pollDelayMs, pollScreenshot, runIdFromState, type ScreenshotQueryState, startScreenshot } from "@/lib/screenshot";
import type { ScreenshotData } from "@domainstack/types";
import { Button } from "@domainstack/ui/button";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
export interface UseScreenshotResult {
  data: ScreenshotData | null;
  isLoading: boolean;
  error: Error | null;
  hasFailed: boolean;
}
function ScreenshotPlaceholder(t0) {
  const $ = _c(7);
  const {
    isLoading,
    blocked,
    aspectClassName,
    onReload
  } = t0;
  const t1 = `h-auto w-full ${aspectClassName} flex items-center justify-center bg-muted/50`;
  let t2;
  if ($[0] !== blocked || $[1] !== isLoading || $[2] !== onReload) {
    t2 = <div className="flex items-center gap-2 text-xs text-muted-foreground [&_svg]:size-4" aria-live="polite">{isLoading ? <><Spinner />Taking screenshot…</> : blocked ? <><IconShieldExclamation />Screenshot unavailable for this domain.</> : <><IconCircleX /><span>Unable to take a screenshot.</span>{onReload ? <Button variant="link" size="xs" className="text-xs text-foreground" onClick={onReload}>Reload preview</Button> : null}</>}</div>;
    $[0] = blocked;
    $[1] = isLoading;
    $[2] = onReload;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function withReloadToken(url: string, reloadCount: number): string {
  if (reloadCount === 0) return url;
  try {
    const reloadUrl = new URL(url);
    reloadUrl.searchParams.set("domainstack-reload", String(reloadCount));
    return reloadUrl.toString();
  } catch {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}domainstack-reload=${reloadCount}`;
  }
}
function ScreenshotImage(t0) {
  const $ = _c(20);
  const {
    domain,
    url,
    width,
    height,
    imageClassName,
    aspectClassName
  } = t0;
  const [reloadCount, setReloadCount] = useState(0);
  const [hasFailed, setHasFailed] = useState(false);
  if (hasFailed) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = () => {
        setReloadCount(_temp);
        setHasFailed(false);
      };
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== aspectClassName) {
      t2 = <ScreenshotPlaceholder isLoading={false} blocked={false} aspectClassName={aspectClassName} onReload={t1} />;
      $[1] = aspectClassName;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const t1 = `https://${domain}`;
  let t2;
  if ($[3] !== reloadCount || $[4] !== url) {
    t2 = withReloadToken(url, reloadCount);
    $[3] = reloadCount;
    $[4] = url;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `Homepage preview of ${domain}`;
  let t4;
  if ($[6] !== aspectClassName || $[7] !== imageClassName) {
    t4 = cn("h-auto w-full object-cover", aspectClassName, imageClassName);
    $[6] = aspectClassName;
    $[7] = imageClassName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setHasFailed(true);
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== height || $[11] !== reloadCount || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== width) {
    t6 = <Image key={reloadCount} src={t2} alt={t3} width={width} height={height} className={t4} unoptimized={true} priority={false} draggable={false} onError={t5} />;
    $[10] = height;
    $[11] = reloadCount;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = width;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t1 || $[18] !== t6) {
    t7 = <a href={t1} target="_blank" rel="noopener">{t6}</a>;
    $[17] = t1;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}

/** One step of the start/poll loop, folding transient failures into the backoff states. */
function _temp(current) {
  return current + 1;
}
async function fetchNextState(current: ScreenshotQueryState | undefined, domainId: string | undefined, domain: string): Promise<ScreenshotQueryState> {
  const runId = runIdFromState(current);
  const attempt = current?.status === "retrying" ? current.attempt : 0;
  if (!runId && !domainId) {
    // Deterministic (the `enabled` guard already requires a domainId) —
    // never retryable, so this throws instead of feeding the backoff loop.
    const error = new Error("Screenshot domain ID is missing");
    posthogClient.captureException(error, {
      domain
    });
    throw error;
  }
  try {
    return runId ? await pollScreenshot(runId) : await startScreenshot(domainId as string);
  } catch (err) {
    if (attempt + 1 >= MAX_CONSECUTIVE_FAILURES) {
      posthogClient.captureException(err, {
        domain
      });
      return {
        status: "failed",
        error: err instanceof Error ? err.message : "Screenshot request failed",
        recoverable: true
      };
    }
    return {
      status: "retrying",
      attempt: attempt + 1,
      runId
    };
  }
}

/**
 * Analytics and toasts for a state change. Called from the query function, which
 * runs once per fetch for all observers, so each transition is reported once.
 */
function reportTransition(prev: ScreenshotQueryState | undefined, next: ScreenshotQueryState, {
  domain,
  domainId
}: {
  domain: string;
  domainId?: string;
}) {
  // A retry or rate limit mid-run keeps the run id, so compare only that: one
  // request event per run, however many transient polls it takes.
  if (next.status === "running" && runIdFromState(prev) !== next.runId) {
    posthogClient.capture("screenshot_requested", {
      domain
    });
  } else if (next.status === "completed") {
    posthogClient.capture(next.source === "cache" ? "screenshot_loaded_from_cache" : "screenshot_loaded_from_api", {
      domain
    });
  } else if (next.status === "rate_limited" && !(prev?.status === "rate_limited" && prev.runId === next.runId && prev.retryAfter === next.retryAfter)) {
    toast.error("Too many requests", {
      id: `screenshot-rate-limited-${domainId ?? domain}`,
      description: `Retrying in ${next.retryAfter} second${next.retryAfter !== 1 ? "s" : ""}.`
    });
    posthogClient.capture("screenshot_rate_limited", {
      domain,
      retryAfter: next.retryAfter
    });
  }
}
const refetchUnlessWaiting = (query: {
  state: {
    data: ScreenshotQueryState | undefined;
  };
}) => !isTerminalState(query.state.data) && !isAwaitingScheduledRetry(query.state.data);

/**
 * Hook to fetch a screenshot for a domain.
 * Call this in a component that stays mounted to keep polling active.
 */
export function useScreenshot(t0) {
  const $ = _c(21);
  const {
    domain,
    domainId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[0] !== domain || $[1] !== domainId) {
    t2 = getScreenshotQueryKey(domain, domainId);
    $[0] = domain;
    $[1] = domainId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const queryKey = t2;
  let t3;
  if ($[3] !== domain || $[4] !== domainId || $[5] !== queryClient || $[6] !== queryKey) {
    t3 = async () => {
      const current = queryClient.getQueryData(queryKey);
      if (isTerminalState(current)) {
        return current;
      }
      const next = await fetchNextState(current, domainId, domain);
      reportTransition(current, next, {
        domain,
        domainId
      });
      return next;
    };
    $[3] = domain;
    $[4] = domainId;
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = enabled && !!domainId;
  let t5;
  if ($[8] !== queryKey || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      queryKey,
      queryFn: t3,
      enabled: t4,
      retry: false,
      staleTime: _temp2,
      refetchOnMount: refetchUnlessWaiting,
      refetchOnWindowFocus: refetchUnlessWaiting,
      refetchOnReconnect: refetchUnlessWaiting,
      refetchInterval: _temp3,
      refetchIntervalInBackground: true
    };
    $[8] = queryKey;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const screenshotQuery = useQuery(t5);
  const state = screenshotQuery.data;
  const data = state?.status === "completed" ? state.data : null;
  const hasFailed = state?.status === "failed";
  let t6;
  if ($[12] !== hasFailed || $[13] !== screenshotQuery.error || $[14] !== state) {
    t6 = hasFailed ? new Error(state.error) : screenshotQuery.error ?? null;
    $[12] = hasFailed;
    $[13] = screenshotQuery.error;
    $[14] = state;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const error = t6;
  const isLoading = enabled && (!domainId || !data && !hasFailed);
  let t7;
  if ($[16] !== data || $[17] !== error || $[18] !== hasFailed || $[19] !== isLoading) {
    t7 = {
      data,
      isLoading,
      error,
      hasFailed
    };
    $[16] = data;
    $[17] = error;
    $[18] = hasFailed;
    $[19] = isLoading;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}

/**
 * Display component for screenshot results.
 * Use with useScreenshot hook for data fetching.
 */
function _temp3(query_0) {
  return pollDelayMs(query_0.state.data);
}
function _temp2(query) {
  return isTerminalState(query.state.data) ? Number.POSITIVE_INFINITY : 0;
}
export function Screenshot(t0) {
  const $ = _c(12);
  const {
    domain,
    data,
    isLoading,
    className,
    width: t1,
    height: t2,
    imageClassName,
    aspectClassName: t3
  } = t0;
  const width = t1 === undefined ? 1200 : t1;
  const height = t2 === undefined ? 630 : t2;
  const aspectClassName = t3 === undefined ? "aspect-[1200/630]" : t3;
  const blocked = data?.blocked ?? false;
  const url = blocked ? null : data?.url ?? null;
  let t4;
  if ($[0] !== aspectClassName || $[1] !== blocked || $[2] !== domain || $[3] !== height || $[4] !== imageClassName || $[5] !== isLoading || $[6] !== url || $[7] !== width) {
    t4 = url ? <ScreenshotImage key={url} domain={domain} url={url} width={width} height={height} imageClassName={imageClassName} aspectClassName={aspectClassName} /> : <ScreenshotPlaceholder isLoading={isLoading} blocked={blocked} aspectClassName={aspectClassName} />;
    $[0] = aspectClassName;
    $[1] = blocked;
    $[2] = domain;
    $[3] = height;
    $[4] = imageClassName;
    $[5] = isLoading;
    $[6] = url;
    $[7] = width;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== className || $[10] !== t4) {
    t5 = <div className={className}>{t4}</div>;
    $[9] = className;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}